import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Gauge, Loader2, RefreshCw } from "lucide-react";
import {
  Alert,
  Button,
  ErrorPage,
  FeedbackState,
  Mono,
  PageContent,
  PageHeader,
  PageShell,
  Tabs,
} from "@h2j-data-team/ui";
import type { CapacitySnapshot } from "@repo/shared";

import { toast } from "@/components/shared";
import { BackLink } from "@/components/back-link";
import { supportCode } from "@/components/error-screen";
import { getErrorMessage } from "@/lib/api-client";
import { useFormatDate } from "@/hooks/use-format-date";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";
import {
  isCapacityActive,
  latestCapacitySnapshotQueryOptions,
  useStartCapacityCollection,
} from "@/features/capacity/api";
import {
  CapacityGaugeCard,
  CapacityByAppTable,
  CapacityByFileTable,
  CapacityByDayTable,
  CapacitySkeleton,
} from "@/components/capacity";

type Tab = "byApp" | "byFile" | "byDay";

export const Route = createFileRoute("/_authenticated/capacity/$connectionId")({
  validateSearch: (search: Record<string, unknown>): { tab: Tab } => ({
    tab: search.tab === "byFile" || search.tab === "byDay" ? search.tab : "byApp",
  }),
  staticData: { pageTitle: "breadcrumbs.capacity" },
  component: CapacityDetailPage,
});

function CapacityDetailPage() {
  const { t } = useTranslation("capacity");
  const { t: tc } = useTranslation("common");
  const { formatDate } = useFormatDate();
  const { connectionId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data: connections, isLoading: connectionsLoading } = useQuery(connectionsQueryOptions);
  // The org's own list is the authority: an id that isn't in it belongs to
  // another organization (or to nothing at all).
  const connection = connections?.find((c) => c.id === connectionId);

  const {
    data: latest,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(latestCapacitySnapshotQueryOptions(connectionId));

  const startMutation = useStartCapacityCollection();

  // While a collection runs, the connection's newest snapshot IS the empty
  // pending one — keep the last completed one on screen instead of blanking the
  // page under the status strip. Scoped to its own connection so switching
  // tenants never shows the other one's numbers.
  const [lastCompleted, setLastCompleted] = useState<CapacitySnapshot | null>(null);
  if (latest?.status === "completed" && lastCompleted?.id !== latest.id) {
    setLastCompleted(latest);
  }
  const shown =
    latest?.status === "completed"
      ? latest
      : lastCompleted?.connectionId === connectionId
        ? lastCompleted
        : undefined;

  const collecting = isCapacityActive(latest?.status);

  // The day the ranking measures is the honest denominator for "% of day".
  const dayTotalBytes = useMemo(() => {
    if (!shown) return 0;
    return shown.dailyTotals
      .filter((d) => !shown.detailDate || d.date === shown.detailDate)
      .reduce((sum, d) => sum + d.bytes, 0);
  }, [shown]);

  const handleCollect = () => {
    startMutation.mutate(connectionId, {
      onSuccess: ({ created }) =>
        toast.success(t(created ? "toast.started" : "toast.alreadyRunning")),
      onError: (err) => toast.error(getErrorMessage(err, t("toast.startFailed"))),
    });
  };

  const setTab = (next: Tab) => navigate({ search: (prev) => ({ ...prev, tab: next }) });

  const back = <BackLink to="/capacity" label={t("detail.back")} />;
  const collectBusy = collecting || startMutation.isPending;
  const collectButton = (label: string) => (
    <Button size="sm" onClick={handleCollect} loading={collectBusy}>
      {!collectBusy && <RefreshCw />}
      {label}
    </Button>
  );
  const header = (
    <PageHeader
      title={connection?.name || t("title")}
      description={connection?.baseUrl}
      back={back}
      actions={collectButton(collecting ? t("collecting") : t("collect"))}
    />
  );

  if (connectionsLoading || isLoading) {
    return (
      <PageShell header={header}>
        <PageContent>
          <CapacitySkeleton />
        </PageContent>
      </PageShell>
    );
  }

  if (!connection) {
    return (
      <ErrorPage
        kind="not-found"
        header={<PageHeader title={t("title")} back={back} />}
        title={t("empty.notFound.title")}
        description={t("empty.notFound.description")}
        actions={
          <Button asChild>
            <Link to="/capacity" search={{ q: undefined }}>
              {t("empty.notFound.action")}
            </Link>
          </Button>
        }
      />
    );
  }

  if (isError) {
    return (
      <ErrorPage
        kind="load-failed"
        header={header}
        title={t("error.load")}
        supportCode={supportCode(error, tc("error.supportCode"))}
        actions={
          <Button variant="outline" onClick={() => void refetch()}>
            <RefreshCw />
            {tc("tryAgain")}
          </Button>
        }
      />
    );
  }

  if (!latest && !shown) {
    return (
      <PageShell header={header}>
        <PageContent>
          <FeedbackState
            state="empty"
            icon={<Gauge />}
            title={t("empty.noSnapshot.title")}
            description={t("empty.noSnapshot.description")}
            action={
              <Button onClick={handleCollect} loading={startMutation.isPending}>
                {t("collect")}
              </Button>
            }
          />
        </PageContent>
      </PageShell>
    );
  }

  return (
    <PageShell header={header}>
      <PageContent>
        <div className="flex flex-col gap-5">
          {collecting && (
            <Alert
              tone="info"
              title={t("status.collecting")}
              icon={<Loader2 className="animate-spin" />}
            >
              {shown ? t("status.stale") : t("status.collectingHint")}
            </Alert>
          )}

          {latest?.status === "failed" && (
            <Alert tone="danger" title={t("status.failedTitle")}>
              {latest.error ?? t("status.unknownError")}
            </Alert>
          )}

          {/* First collection: nothing to show under the strip yet, so the page
              holds its shape instead of going blank. */}
          {collecting && !shown && <CapacitySkeleton />}

          {shown && <CapacityGaugeCard snapshot={shown} />}

          {/* The per-app detail rides a separate (QVF) path that can fail on its
              own — the aggregate above is still valid, so the tabs give way to the
              reason instead of the whole page failing. */}
          {shown && !shown.detailAvailable && (
            <Alert tone="warning" title={t("detailUnavailable.title")}>
              <div className="flex flex-col gap-2">
                <p>{t("detailUnavailable.description")}</p>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider">
                    {t("detailUnavailable.causeLabel")}
                  </p>
                  <Mono className="mt-1 block rounded-md bg-surface px-3 py-2 text-xs text-dim">
                    {shown.detailError ?? t("detailUnavailable.unknownCause")}
                  </Mono>
                </div>
                <p className="text-xs">{t("detailUnavailable.retryHint")}</p>
              </div>
            </Alert>
          )}

          {shown?.detailAvailable && (
            <div className="flex flex-col gap-3">
              <Tabs
                label={t("tabs.label")}
                value={tab}
                onValueChange={(next) => setTab(next as Tab)}
                items={[
                  {
                    value: "byApp",
                    label: t("tabs.byApp"),
                    // key: switching connection is param-only navigation and does
                    // not remount, so without it one tenant's filters and drilled
                    // folder would carry into the next.
                    content: (
                      <CapacityByAppTable
                        key={shown.id}
                        snapshotId={shown.id}
                        dayTotalBytes={dayTotalBytes}
                      />
                    ),
                  },
                  {
                    value: "byFile",
                    label: t("tabs.byFile"),
                    content: <CapacityByFileTable key={shown.id} snapshotId={shown.id} />,
                  },
                  {
                    value: "byDay",
                    label: t("tabs.byDay"),
                    content: <CapacityByDayTable dailyTotals={shown.dailyTotals} />,
                  },
                ]}
              />

              {/* These tabs read the consumption QVF, which Qlik refreshes once a
                  day; the gauge above reads the live aggregate. The newest day can
                  therefore sit below the gauge without either being wrong. */}
              <div className="flex flex-col gap-1 text-xs text-faint">
                {shown.detailDate && (
                  <p className="font-mono">
                    {t("measuredDay", { date: formatDate(`${shown.detailDate}T00:00:00`) })}
                  </p>
                )}
                <p>{t("sourceNote")}</p>
              </div>
            </div>
          )}
        </div>
      </PageContent>
    </PageShell>
  );
}
